import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { linkPillStyles } from "@theme/styles";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";

interface MarkdownRendererProps {
  content: string;
  isUser?: boolean;
}

const MarkdownRenderer = ({
  content,
  isUser = false
}: MarkdownRendererProps) => {
  const theme = useTheme();

  const botFontSize = {
    xs: "16px",
    sm: `${theme.customTypography.botMessage.fontSize}px`
  };

  // Lightweight linkify: convert plain URLs to markdown links to avoid extra deps
  const linkifiedContent = (content || "").replace(
    /(https?:\/\/[^\s)]+|www\.[^\s)]+)/g,
    (match, _p1, offset, full) => {
      const start = offset as number;
      const prevChar = start > 0 ? (full as string)[start - 1] : "";
      const _nextChar = (full as string)[start + match.length];
      // Do not linkify if already inside markdown link parentheses
      if (prevChar === "(") return match;
      // Normalize URL
      let prefix = "";
      let raw = match;
      if (raw.startsWith("@")) {
        prefix = "@";
        raw = raw.slice(1);
      }
      const url = raw.startsWith("http") ? raw : `https://${raw}`;
      return `${prefix}[${raw}](${url})`;
    }
  );

  // Fix asterisks inside bold text: escape asterisks that are inside **...** blocks
  const fixedBoldContent = linkifiedContent.replace(
    /\*\*([\s\S]*?)\*\*/g,
    (match, content) => {
      const escapedContent = content.replace(/\*/g, "\\*");
      return `**${escapedContent}**`;
    }
  );

  const customComponents = {
    // Encabezados
    h1: ({ children }: any) => (
      <Typography
        variant="h5"
        component="h1"
        sx={{
          fontWeight: "bold",
          mb: 1,
          mt: 1,
          color: isUser ? "primary.contrastText" : "text.primary"
        }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }: any) => (
      <Typography
        variant="h6"
        component="h2"
        sx={{
          fontWeight: "bold",
          mb: 1,
          mt: 1,
          color: isUser ? "primary.contrastText" : "text.primary"
        }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }: any) => (
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          fontWeight: "bold",
          mb: 0.5,
          mt: 1,
          color: isUser ? "primary.contrastText" : "text.primary"
        }}
      >
        {children}
      </Typography>
    ),

    // Párrafos
    p: ({ children }: any) => (
      <Typography
        variant="body1"
        sx={{
          mb: 1,
          textAlign: "left",
          overflowWrap: "break-word",
          color: isUser
            ? "primary.contrastText"
            : (theme) => theme.palette.customColors.textPrimary,
          // Mobile styles from Figma
          ...(isUser
            ? {}
            : {
                fontFamily: (theme) => theme.typography.fontFamily,
                fontWeight: {
                  xs: 325, // Book
                  sm: theme.customTypography.botMessage.fontWeight
                },
                fontSize: botFontSize,
                lineHeight: {
                  xs: "24px", // 24px in mobile
                  sm: theme.customTypography.botMessage.lineHeight
                },
                letterSpacing: {
                  xs: "0%", // 0% in mobile
                  sm: theme.customTypography.botMessage.letterSpacing
                }
              })
        }}
      >
        {children}
      </Typography>
    ),

    // Listas
    ul: ({ children }: any) => (
      <Box
        component="ul"
        sx={{
          pl: { xs: 2.5, sm: 2 }, // More padding on mobile for bullets
          mb: 1,
          color: isUser ? "primary.contrastText" : "text.primary",
          "& ul, & ol": {
            fontSize: botFontSize
          },
          "& li": {
            fontSize: botFontSize
          }
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box
        component="ol"
        sx={{
          pl: { xs: 2.5, sm: 2 }, // More padding on mobile for bullets
          mb: 1,
          color: isUser ? "primary.contrastText" : "text.primary",
          "& ul, & ol": {
            fontSize: botFontSize
          },
          "& li": {
            fontSize: botFontSize
          }
        }}
      >
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Typography
        component="li"
        variant="body1"
        sx={{
          fontSize: botFontSize,
          mb: 0.5,
          color: isUser ? "primary.contrastText" : "text.primary",
          "& ul li, & ol li": {
            fontSize: botFontSize
          }
        }}
      >
        {children}
      </Typography>
    ),

    // Código en línea
    code: ({ inline, children }: any) => {
      if (inline) {
        return (
          <Box
            component="code"
            sx={{
              backgroundColor: isUser
                ? "rgba(255,255,255,0.2)"
                : theme.palette.grey[100],
              color: isUser
                ? "primary.contrastText"
                : theme.palette.text.primary,
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.9em"
            }}
          >
            {children}
          </Box>
        );
      }
      return children;
    },

    // Bloques de código
    pre: ({ children }: any) => (
      <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden" }}>{children}</Box>
    ),

    // Texto en negrita
    strong: ({ children }: any) => (
      <Box
        component="strong"
        sx={{
          fontFamily: "Ringside Narrow",
          fontWeight: {
            xs: 700, // Bold in mobile
            sm: "bold"
          },
          fontSize: botFontSize,
          lineHeight: {
            xs: "24px", // 24px in mobile
            sm: theme.customTypography.botMessage.lineHeight
          },
          letterSpacing: {
            xs: "0%", // 0% in mobile
            sm: theme.customTypography.botMessage.letterSpacing
          },
          color: isUser
            ? "primary.contrastText"
            : theme.palette.customColors.textPrimary
        }}
      >
        {children}
      </Box>
    ),

    // Texto en cursiva
    em: ({ children }: any) => (
      <Box
        component="em"
        sx={{
          fontStyle: "italic",
          color: isUser ? "primary.contrastText" : "text.primary"
        }}
      >
        {children}
      </Box>
    ),

    // Enlaces
    a: ({ children, href }: any) => {
      let label = String(children);
      try {
        const url = new URL(href);
        label = url.hostname;
      } catch {}
      return (
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkPillStyles(theme)}
        >
          {label}
        </Box>
      );
    },

    // Citas
    blockquote: ({ children }: any) => (
      <Box
        sx={{
          borderLeft: `4px solid ${isUser ? "rgba(255,255,255,0.5)" : theme.palette.primary.main}`,
          pl: 2,
          py: 1,
          mb: 1,
          backgroundColor: isUser
            ? "rgba(255,255,255,0.1)"
            : theme.palette.grey[50],
          borderRadius: 1
        }}
      >
        {children}
      </Box>
    ),

    // Líneas horizontales
    hr: () => (
      <Box
        sx={{
          height: 1,
          backgroundColor: isUser
            ? "rgba(255,255,255,0.3)"
            : theme.palette.divider,
          my: 2,
          border: "none"
        }}
      />
    )
  };

  return (
    <ReactMarkdown
      components={{
        ...customComponents,
        code({ node: _node, inline, className, children, ...props }: any) {
          const match = /language-(\w+)/.exec(className || "");
          const language = match ? match[1] : "";

          return !inline && language ? (
            <SyntaxHighlighter
              style={tomorrow}
              language={language}
              PreTag="div"
              customStyle={{
                margin: 0,
                borderRadius: theme.spacing(1),
                fontSize: "0.875rem"
              }}
              {...props}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          ) : (
            <Box
              component="code"
              sx={{
                backgroundColor: isUser
                  ? "rgba(255,255,255,0.2)"
                  : theme.palette.grey[100],
                color: isUser
                  ? "primary.contrastText"
                  : theme.palette.text.primary,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.9em"
              }}
              {...props}
            >
              {children}
            </Box>
          );
        }
      }}
    >
      {fixedBoldContent}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
